import { supabase } from '@/integrations/supabase/client';
import { getCurrentSession } from '@/lib/rpc/auth-facade';

export type KycStartOptions = {
  endorsement?: 'base' | 'sepa';
  redirect_url?: string;
  force_new?: boolean;
};

export type KycStartResult = {
  url: string;
  kyc_link: string;
  tos_link?: string;
  expires_at?: string | null;
  allowed_hosts: string[];
  endorsement: string;
};

export class KycStartError extends Error {
  readonly status?: number;
  readonly code?: string;
  readonly retryAfterSeconds?: number;

  constructor(message: string, options: { status?: number; code?: string; retryAfterSeconds?: number } = {}) {
    super(message);
    this.name = 'KycStartError';
    this.status = options.status;
    this.code = options.code;
    this.retryAfterSeconds = options.retryAfterSeconds;
  }
}

// Debounce/semaphore to prevent double-clicks and race conditions
let currentKycStartPromise: Promise<KycStartResult> | null = null;

const DEFAULT_ALLOWED_HOSTS = [
  'bridge.xyz',
  'kyc.bridge.xyz',
  'app.bridge.xyz',
  'withpersona.com',
  'bridge.withpersona.com'
];

function parseError(error: unknown): { status: number; code: string; message: string; retryAfter?: number } {
  const base = { status: 500, code: 'kyc_start_failed', message: 'Не удалось начать проверку личности.' };
  if (!error || typeof error !== 'object') {
    return base;
  }

  const record = error as Record<string, unknown>;
  const status = typeof record.status === 'number' ? record.status : base.status;
  let message = typeof record.message === 'string' ? record.message : base.message;
  let code = typeof record.name === 'string' ? record.name : base.code;
  let retryAfter: number | undefined;

  const context = record.context && typeof record.context === 'object' ? (record.context as Record<string, unknown>) : null;
  if (context) {
    if (typeof context.message === 'string') {
      message = context.message;
    }
    if (typeof context.code === 'string') {
      code = context.code;
    }
    if (typeof context.retry_after === 'number') {
      retryAfter = context.retry_after;
    }
  }

  const details = record.details && typeof record.details === 'object' ? (record.details as Record<string, unknown>) : null;
  if (!retryAfter && details && typeof details.retry_after === 'number') {
    retryAfter = details.retry_after;
  }

  const messageText = typeof record.message === 'string' ? record.message : null;
  if (messageText) {
    try {
      const parsed = JSON.parse(messageText) as Record<string, unknown>;
      const parsedError = parsed.error && typeof parsed.error === 'object' ? (parsed.error as Record<string, unknown>) : null;
      if (parsedError) {
        if (typeof parsedError.message === 'string') {
          message = parsedError.message;
        }
        if (typeof parsedError.code === 'string') {
          code = parsedError.code;
        }
      }
      if (typeof parsed.retry_after === 'number') {
        retryAfter = parsed.retry_after;
      }
      if (typeof parsed.status === 'number') {
        return { status: parsed.status, code, message, retryAfter };
      }
    } catch {
      // ignore parse errors
    }
  }

  return { status, code, message, retryAfter };
}

function isUrlAllowed(url: string | null | undefined, allowedHosts: string[]): boolean {
  if (!url) return false;

  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.toLowerCase();

    return allowedHosts.some(allowedHost => {
      const normalizedHost = allowedHost.toLowerCase();
      // Exact match or subdomain match (e.g., 'bridge.xyz' allows 'kyc.bridge.xyz')
      return hostname === normalizedHost || hostname.endsWith(`.${normalizedHost}`);
    });
  } catch {
    return false;
  }
}

function parseKycResult(data: unknown, fallbackEndorsement: string | undefined): KycStartResult {
  const root = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};

  // Handle nested response: { data: { link: { kyc_link: ... } } }
  const innerData = root.data && typeof root.data === 'object'
    ? (root.data as Record<string, unknown>)
    : root;

  const payload = innerData.link && typeof innerData.link === 'object'
    ? (innerData.link as Record<string, unknown>)
    : innerData;

  const url = typeof payload.kyc_link === 'string'
    ? payload.kyc_link
    : typeof payload.url === 'string'
      ? payload.url
      : null;

  if (!url) {
    console.error('[parseKycResult] Failed to extract URL from:', { root, innerData, payload });
    throw new KycStartError('Invalid response from KYC service', { code: 'invalid_response' });
  }

  const allowedHosts = Array.isArray(payload.allowed_hosts)
    ? payload.allowed_hosts.filter((host): host is string => typeof host === 'string' && host.length > 0)
    : DEFAULT_ALLOWED_HOSTS;

  const finalAllowedHosts = allowedHosts.length > 0 ? allowedHosts : DEFAULT_ALLOWED_HOSTS;

  // Validate kyc_link against allowlist
  if (!isUrlAllowed(url, finalAllowedHosts)) {
    console.error('[parseKycResult] kyc_link not in allowlist:', { url, allowed: finalAllowedHosts });
    throw new KycStartError('KYC link from untrusted source', { code: 'untrusted_link' });
  }

  // Validate tos_link if present
  const tosLink = typeof payload.tos_link === 'string' && payload.tos_link.length > 0
    ? payload.tos_link
    : null;

  if (tosLink && !isUrlAllowed(tosLink, finalAllowedHosts)) {
    console.warn('[parseKycResult] tos_link not in allowlist, ignoring:', { tosLink, allowed: finalAllowedHosts });
    // Don't throw - just ignore the tos_link (ToS is optional)
  }

  const expiresAt = typeof payload.expires_at === 'string' ? payload.expires_at : null;
  const endorsement = typeof payload.endorsement === 'string'
    ? payload.endorsement
    : fallbackEndorsement ?? 'base';

  return {
    url,
    kyc_link: url,
    tos_link: tosLink && isUrlAllowed(tosLink, finalAllowedHosts) ? tosLink : undefined,
    expires_at: expiresAt,
    allowed_hosts: finalAllowedHosts,
    endorsement,
  };
}

function getDefaultRedirectUrl(): string {
  if (typeof window !== 'undefined' && window.location) {
    try {
      return new URL('/verify', window.location.origin).toString();
    } catch {
      return 'https://www.supabank.xyz/verify';
    }
  }
  return 'https://www.supabank.xyz/verify';
}

/**
 * Unified KYC starter that handles email resolution from session
 * and provides consistent error handling across the app
 */
export async function startKyc(options: KycStartOptions = {}): Promise<KycStartResult> {
  const { endorsement, redirect_url, force_new = false } = options;

  if (!force_new && currentKycStartPromise) {
    return currentKycStartPromise;
  }

  const executor = (async () => {
    const sessionData = await getCurrentSession();

    if (!sessionData?.user?.email) {
      throw new KycStartError('No authenticated session found', { status: 401, code: 'unauthorized' });
    }

    if (!sessionData.access_token) {
      throw new KycStartError('Missing authentication token for KYC start', { status: 401, code: 'unauthorized' });
    }

    const redirectUri = redirect_url || getDefaultRedirectUrl();

    const requestBody: Record<string, unknown> = {
      action: 'start',  // Hub routing: action=start
      redirect_uri: redirectUri,
      force_new,
      email: sessionData.user.email,
    };

    if (endorsement) {
      requestBody.endorsement = endorsement;
      requestBody.endorsements = [endorsement];
    }

    // NEW: Call unified KYC Hub with explicit token using direct fetch
    // CRITICAL: supabase.functions.invoke may not pass headers correctly, use direct fetch
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    if (!supabaseUrl) {
      throw new KycStartError('Supabase URL not configured', { status: 500, code: 'config_error' });
    }

    // eslint-disable-next-line internal/no-direct-edge-fetch -- Special case: KYC start endpoint
    const response = await fetch(`${supabaseUrl}/functions/v1/kyc`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionData.access_token}`,
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY || ''
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorData: any;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { message: errorText };
      }
      
      const parsed = {
        status: response.status,
        code: errorData?.error?.code || 'unknown_error',
        message: errorData?.error?.message || errorData?.message || 'KYC start failed'
      };
      if (parsed.status === 401 || parsed.status === 403) {
        throw new KycStartError('Авторизация истекла. Войдите заново.', {
          status: parsed.status,
          code: parsed.code ?? 'unauthorized'
        });
      }

      if (parsed.status === 429) {
        const retrySeconds = parsed.retryAfter && parsed.retryAfter > 0 ? parsed.retryAfter : 30;
        throw new KycStartError(`Провайдер занят. Повтор через ${retrySeconds} сек.`, {
          status: parsed.status,
          code: parsed.code ?? 'rate_limited',
          retryAfterSeconds: retrySeconds,
        });
      }

      if (parsed.status >= 500) {
        throw new KycStartError('KYC provider is unavailable. Please try again later.', {
          status: parsed.status,
          code: parsed.code,
        });
      }

      throw new KycStartError(parsed.message || 'KYC service error.', {
        status: parsed.status,
        code: parsed.code,
      });
    }

    const data = await response.json();
    return parseKycResult(data, endorsement);
  })();

  currentKycStartPromise = executor;

  try {
    return await executor;
  } finally {
    currentKycStartPromise = null;
  }
}

export async function fetchBridgeTosLink(): Promise<string> {
  const sessionData = await getCurrentSession();

  if (!sessionData?.access_token) {
    throw new KycStartError('Missing authentication token for ToS link', { status: 401, code: 'unauthorized' });
  }

  // TECH DEBT CLEANUP: Removed tos_links endpoint call that always returns 404 for new users.
  // Bridge API doesn't have a standalone ToS endpoint - ToS link is always part of KYC link response.
  // Direct approach: use startKyc() which returns tos_link from Bridge KYC link creation.
  try {
    const kycResult = await startKyc({ force_new: false }); // Don't force new if not needed
    if (kycResult.tos_link) {
      return kycResult.tos_link;
    }
    throw new KycStartError('KYC link generated but no ToS link found', { code: 'tos_link_missing_in_kyc' });
  } catch (error) {
    if (error instanceof KycStartError) throw error;
    throw new KycStartError('Failed to fetch ToS link', { code: 'tos_fetch_failed' });
  }
}
